import { Box, DialogProps, Typography } from '@mui/material';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { AppContext } from '../../app/context';
import illustration from '../../assets/img/hello-2-illus.svg';
import challengeIllustration from '../../assets/img/trophy-illus-3.svg';
import { Button, Dialog, PremiumAvatar } from '../../components';
import useHighlightedEventsQuery from '../../hooks/use-highlighted-events-query';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';

function WelcomeDialog({
  ...props
}: Omit<DialogProps, 'onClose'> & { onClose: () => void }): React.JSX.Element {
  const {
    partner: {
      current: currentPartner,
      getStartedProgression: getStartedAdminProgression,
      type: partnerType,
    },
    user: { employee, getStartedProgression: getStartedEmployeeProgression },
  } = useContext(AppContext);
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { data: highlightedEvents } = useHighlightedEventsQuery(currentPartner, geogroup);

  const highlightedEvent = highlightedEvents?.[0];

  const { state } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Dialog fullWidth maxWidth="sm" {...props}>
      <Box display="flex" flexDirection="column" gap={3}>
        {state?.challengeId && highlightedEvent ? (
          <>
            <Box>
              <img src={highlightedEvent.icon || challengeIllustration} style={{ maxHeight: 80 }} />
            </Box>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography fontSize="1.5rem" fontWeight={700}>
                {t('companies.welcome_dialog.title_event', {
                  partnerTitle: currentPartner?.title || '',
                  eventTitle: highlightedEvent.title,
                })}{' '}
                <span style={{ color: '#2ac682' }}>✓</span>
              </Typography>
              <Typography>
                {highlightedEvent.subtitle || t('commons.highlighted_event_card.description')}
              </Typography>
            </Box>
            <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
              <Button
                color="primary"
                onClick={() => {
                  props.onClose();
                  if (state?.challengeId) {
                    navigate(`/events/${highlightedEvent.id}`);
                  }
                }}
                variant="contained"
              >
                {t('commons.highlighted_event_card.actions.display')}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <img src={illustration} width={160} />
            <Typography fontSize="1.5rem" fontWeight={700}>
              {t(
                employee?.isAdminOrAnimator && partnerType === 'free'
                  ? 'companies.welcome_dialog.title_free'
                  : 'companies.welcome_dialog.title',
              )}
            </Typography>
            {employee?.isAdminOrAnimator && partnerType === 'free' && (
              <Typography>
                Bénéficiez d'ores et déjà et de manière entièrement <b>gratuite</b> de ces services
                :
              </Typography>
            )}
            <Typography
              component="ul"
              display="flex"
              flexDirection="column"
              gap={2}
              paddingLeft={3}
            >
              {employee?.isAdminOrAnimator ? (
                <>
                  <Item>
                    Lancez des <b>challenges vélo</b> et suivez les classements
                  </Item>
                  <Item>
                    Visualisez les <b>statistiques d'utilisation du vélo</b> pour l'ensemble de
                    votre entreprise
                  </Item>
                  {employee.isAdmin && (
                    <Item>
                      Envoyez à vos équipes un questionnaire personnalisé pour leur montrer les{' '}
                      <b>bénéfices de la pratique du vélo</b>
                    </Item>
                  )}
                </>
              ) : (
                <>
                  <Item>Partipez à des challenges avec vos collègues</Item>
                  <Item>Hissez-vous dans les tops des classements</Item>
                  <Item>
                    Suivez les statistiques de trajets vélo domicile-travail et celles de votre
                    entreprise
                  </Item>
                </>
              )}
            </Typography>
            <Box alignItems="center" display="flex" gap={3} justifyContent="flex-end">
              <Button
                color="primary"
                onClick={() => {
                  props.onClose();
                  if (
                    employee?.isAdmin
                      ? getStartedAdminProgression && !getStartedAdminProgression.allDone
                      : getStartedEmployeeProgression && !getStartedEmployeeProgression.allDone
                  ) {
                    navigate('/get-started');
                  }
                }}
                variant="contained"
              >
                {t('companies.actions.here_we_go')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Dialog>
  );
}

function Item({
  isPremium,
  children,
}: {
  children: ReactNode;
  isPremium?: boolean;
}): React.JSX.Element {
  return (
    <li style={{ lineHeight: '24px' }}>
      {children}
      {isPremium && (
        <Box display="inline-block" marginLeft={1}>
          <PremiumAvatar />
        </Box>
      )}
    </li>
  );
}

export default WelcomeDialog;
